const supportedTags = new Set(['P', 'DIV', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'SPAN', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'SUB', 'SUP']);
const blockedTags = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'MATH', 'TEMPLATE']);

// Existing plain notes keep their literal representation. Only supported HTML
// formatting opts into rich rendering; neither links nor media activate it.
export const isRichClientNote = value => /<\/?(?:p|div|br|b|strong|i|em|u|s|strike|ul|ol|li|blockquote|span|h[1-6]|sub|sup)(?:\s|\/?>)/i.test(String(value ?? ''));

export const sanitizeClientNoteHtml = value => {
  // Template contents are inert. Rebuild into a separate tree; never copy
  // attributes, URLs, event handlers, styles or foreign-namespace elements.
  const source = document.createElement('template');
  source.innerHTML = String(value ?? '');
  const output = document.createElement('div');
  const appendSafe = (node, parent) => {
    if (node.nodeType === 3) {
      parent.appendChild(document.createTextNode(node.textContent));
      return;
    }
    if (node.nodeType !== 1 || node.namespaceURI !== 'http://www.w3.org/1999/xhtml' || blockedTags.has(node.tagName)) return;
    const target = supportedTags.has(node.tagName) ? document.createElement(node.tagName.toLowerCase()) : parent;
    if (target !== parent) parent.appendChild(target);
    // Legacy editors often encode emphasis on spans. Convert only these
    // known text styles to semantic tags; never retain a style attribute.
    let contentTarget = target;
    const emphasis = [];
    if (/^(bold|[6-9]00)$/i.test(node.style.fontWeight)) emphasis.push('strong');
    if (node.style.fontStyle === 'italic') emphasis.push('em');
    const decoration = `${node.style.textDecoration} ${node.style.textDecorationLine}`;
    if (/\bunderline\b/.test(decoration)) emphasis.push('u');
    if (/\bline-through\b/.test(decoration)) emphasis.push('s');
    emphasis.forEach(tag => {
      const element = document.createElement(tag);
      contentTarget.appendChild(element);
      contentTarget = element;
    });
    Array.from(node.childNodes).forEach(child => appendSafe(child, contentTarget));
  };
  Array.from(source.content.childNodes).forEach(node => appendSafe(node, output));
  return output.innerHTML;
};

export const clientNoteHasText = value => {
  const source = document.createElement('template');
  source.innerHTML = sanitizeClientNoteHtml(value);
  return Boolean(source.content.textContent.trim());
};

export const serializeRichClientNote = value => {
  const html = sanitizeClientNoteHtml(value);
  // Keep rich provenance after select-all/type removes every formatting tag.
  // Otherwise encoded text would be mistaken for a literal plain note on reopen.
  return isRichClientNote(html) ? html : `<div>${html}</div>`;
};
